import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useContractor } from 'context/ContractorContext';
import { getContractorProfile, getCompanyPayrolls, previewPayroll, getContractorDocuments } from 'services/contractorPortal';
import routes from 'constants/routes';

const fmt     = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '—';

const ContractorDashboard = () => {
    const navigate = useNavigate();
    const { contractorId } = useContractor();

    const { data: contractor } = useQuery({
        queryKey: ['cp-contractor', contractorId],
        queryFn: () => getContractorProfile(contractorId),
        enabled: !!contractorId,
    });

    const { data: allPayrolls = [] } = useQuery({
        queryKey: ['cp-company-payrolls'],
        queryFn: getCompanyPayrolls,
        enabled: !!contractorId,
    });

    const approvedPayrolls = useMemo(() => allPayrolls.filter(p => p.status?.toLowerCase() === 'approved'), [allPayrolls]);

    const previewQueries = useQueries({
        queries: approvedPayrolls.map(p => ({
            queryKey: ['cp-preview', p.id],
            queryFn: () => previewPayroll(p.id),
            enabled: !!contractorId,
            staleTime: 1000 * 60 * 10,
        })),
    });

    const payments = useMemo(() => {
        if (!contractorId) return [];
        const result = [];
        approvedPayrolls.forEach((payroll, i) => {
            const preview = previewQueries[i]?.data;
            const cData = preview?.contractors?.find(c => c.contractorId === contractorId || c.id === contractorId);
            if (cData && (cData.grossPay > 0 || cData.reimbursement > 0)) result.push({ payroll, cData });
        });
        return result.sort((a, b) => new Date(b.payroll.payDate) - new Date(a.payroll.payDate));
    }, [approvedPayrolls, previewQueries, contractorId]);

    const { data: documents = [] } = useQuery({
        queryKey: ['cp-documents', contractorId],
        queryFn: () => getContractorDocuments(contractorId),
        enabled: !!contractorId,
    });

    const c = contractor || {};
    const displayName = c.businessName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
    const address = [c.addressLine1, c.city, c.state, c.zip].filter(Boolean).join(', ');

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Dashboard</h2>
            <p style={{ color: '#6c757d', marginBottom: 24 }}>
                Access your payment history, manage your profile, and view your tax documents — all in one place.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>Contractor ID:</span>
                <span style={{ fontSize: 13, color: '#555', fontFamily: 'monospace' }}>
                    {contractorId ? `••••${contractorId.slice(-6)}` : '—'}
                </span>
            </div>

            {/* Status Card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Active Status</p>
                    <span style={{ backgroundColor: c.active ? '#28a745' : '#6c757d', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                        {c.active ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <div>
                    <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Pay Type</p>
                    <span style={{ backgroundColor: c.payType ? '#0d6efd' : '#adb5bd', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>
                        {c.payType || '—'}
                    </span>
                </div>
                <div>
                    <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Name / Business</p>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{displayName}</span>
                </div>
                {address && (
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Address</p>
                        <span style={{ fontSize: 13 }}>{address}</span>
                    </div>
                )}
                <button
                    onClick={() => navigate(`${routes.CONTRACTOR_PORTAL.HOME}${routes.CONTRACTOR_PORTAL.PROFILE}`)}
                    style={{ background: '#fff', border: '1px solid #E7730A', color: '#E7730A', borderRadius: 8, padding: '8px 20px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    View Profile
                </button>
            </div>

            {/* Payments Table */}
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 16 }}>Recent Payments</h4>
            <div style={{ background: '#fff', borderRadius: 10, marginBottom: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Payday', 'Period', 'Hours', 'Pay Rate', 'Gross Pay', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {payments.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>
                                    {contractorId ? 'No payments found' : 'Loading...'}
                                </td>
                            </tr>
                        ) : payments.slice(0, 5).map((p, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payroll.payDate)}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payroll.startDate)} – {fmtDate(p.payroll.endDate)}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.cData?.hours != null ? p.cData.hours : '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.cData?.payRate ? fmt(p.cData.payRate) : '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#198754' }}>{fmt(p.cData?.grossPay ?? ((p.cData?.hours ?? 0) * (p.cData?.payRate ?? 0)))}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                    <button onClick={() => navigate(`${routes.CONTRACTOR_PORTAL.HOME}${routes.CONTRACTOR_PORTAL.PAYMENTS}`)} style={{ background: 'none', border: 'none', color: '#1a9fbb', cursor: 'pointer', fontWeight: 500 }}>View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <div style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#6c757d' }}>
                    1 – {Math.min(payments.length, 5)} of {payments.length}
                </div>
            </div>

            {/* Documents Table */}
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 16 }}>Tax Documents</h4>
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Label', 'Jurisdiction', 'Year', 'Filed On', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {documents.length === 0 ? (
                            <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>No documents found</td></tr>
                        ) : documents.slice(0, 5).map((d, i) => (
                            <tr key={d.id || i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.label || d.name || '1099-NEC'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.jurisdiction || '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.year || '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.filedAt ? new Date(d.filedAt).toLocaleDateString() : '—'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                    <button onClick={() => navigate(`${routes.CONTRACTOR_PORTAL.HOME}${routes.CONTRACTOR_PORTAL.DOCUMENTS}`)} style={{ background: 'none', border: 'none', color: '#1a9fbb', cursor: 'pointer', fontWeight: 500 }}>View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
};

export default ContractorDashboard;
